import React, { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Alert, Card, Spin, Space } from 'antd'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import { truncate } from 'lodash'
import { useFetchReviewersApproveAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import { useFetchAssignmentSharedWithQuery } from '../../../../../../../store/apis/shared-assignment.api'
import { fetchAssignmentSharedWithSelectors } from '../../../../../../../store/apis/utils/selectors/shared-assignment.api.selectors'
import { useSelector } from 'react-redux'
import assignmentSelectors from '../../../../../../../store/selectors/assignment.selectors'
import userSelectors from '../../../../../../../store/selectors/user.selectors'
import { getColorByIndex } from '../../../../../../../utils/colors.helper'

const BAR_HEIGHT = 36

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload
        return (
            <Card size='small'>
                <Title level={5} style={{ margin: 0, fontSize: '0.9rem' }}>
                    {data.reviewerName}
                </Title>
                <Text>{`${data.percentCompleted}% - ${data.reviewedSubmissions} of ${data.assignedSubmissions} assigned submissions`}</Text>
            </Card>
        )
    }
    return null
}

const CustomBar = props => {
    const { x, y, width, height, payload } = props
    const color = payload.userId ? getColorByIndex(payload.userId).value : getColorByIndex(0).value
    return <rect x={x} y={y} width={width} height={Math.min(height, 48)} fill={color} cursor='pointer' rx={4} ry={4} />
}

const HumanReviewProgressByReviewerChart = ({ token }) => {
    const currentUser = useSelector(userSelectors.user)
    const ownerId = useSelector(assignmentSelectors.ownerId)
    const ownerName = useSelector(assignmentSelectors.ownerName)

    const { data: reviewersData, isFetching: isFetchingReviewers, error: reviewersError } = useFetchReviewersApproveAnalyticsQuery({ token })
    
    const { data: users, isFetching: isFetchingUsers, error: usersError } = useFetchAssignmentSharedWithQuery(
        { token },
        {
            selectFromResult: result => fetchAssignmentSharedWithSelectors.selectOnlyUsers(result),
        },
    )

    // Get reviewer name based on user ID or organization ID
    const getReviewerName = (reviewer) => {
        // Owner case
        if (reviewer.userId === ownerId) {
            return currentUser.id === ownerId ? `${ownerName} (you)` : ownerName
        }
        
        // User case
        if (reviewer.userId) {
            const user = users?.find(user => user.id === reviewer.userId)
            if (user) {
                return currentUser.id === user.id ? `${user.name} (you)` : user.name
            }
            return `Reviewer ${reviewer.userId}`
        }
        
        return 'Unknown Reviewer'
    }

    // Prepare data for the chart, combining reviewer statistics with user details
    const data = useMemo(() => {
        if (!reviewersData) return []
        
        const mappedData = reviewersData.reviewers.map(reviewer => ({
            ...reviewer,
            reviewerName: getReviewerName(reviewer)
        }))
        
        // Sort by completion percentage in descending order
        return [...mappedData].sort((a, b) => b.percentCompleted - a.percentCompleted)
    }, [reviewersData, users, ownerId])

    const chartHeight = Math.max(250, data.length * BAR_HEIGHT + 100)
    const isFetching = isFetchingReviewers || isFetchingUsers
    const error = reviewersError || usersError

    if (error) return <Alert message={error} type='error' showIcon />

    return (
        <Spin spinning={isFetching} tip='Loading...'>
            <ResponsiveContainer width='100%' height={chartHeight}>
                <BarChart data={data} layout='vertical' margin={{ top: 0, right: 12, left: 12, bottom: 36 }}>
                    <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                    <XAxis
                        type='number'
                        domain={[0, 100]}
                        tickFormatter={value => `${value}%`}
                        label={{ 
                            value: 'Completion Rate (% of assigned submissions)', 
                            position: 'bottom', 
                            offset: 15 
                        }}
                    />
                    <YAxis
                        dataKey='reviewerName'
                        type='category'
                        tick={{ fontSize: 12 }}
                        width={144}
                        tickFormatter={value => truncate(value)}
                        label={{ value: 'Reviewers', angle: -90, position: 'left' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                        dataKey='percentCompleted' 
                        shape={<CustomBar />} 
                    />
                </BarChart>
            </ResponsiveContainer>
            
            {reviewersData && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Space direction="vertical">
                        <Text>
                            {`Total reviewed: ${reviewersData.totalReviewedSubmissions} out of ${reviewersData.totalAssignedSubmissions} assigned submissions (${reviewersData.totalPercentCompleted}%)`}
                        </Text>
                    </Space>
                </div>
            )}
        </Spin>
    )
}

export default HumanReviewProgressByReviewerChart 